import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Play, Pause, RotateCcw, Clock } from "lucide-react";
import { playTimeUp, playTick, playClick } from "../lib/sound";

const formatTime = (s) => {
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
};

export const DebateTimer = ({
  durationSeconds,
  label,
  hint,
  accentColor = "#FFD700",
  resetSignal,
  testIdPrefix = "timer",
}) => {
  const [seconds, setSeconds] = useState(durationSeconds);
  const [running, setRunning] = useState(false);
  const intervalRef = useRef(null);

  // Stop & reset when round changes
  useEffect(() => {
    setRunning(false);
    setSeconds(durationSeconds);
  }, [resetSignal, durationSeconds]);

  useEffect(() => {
    if (!running) return;
    intervalRef.current = setInterval(() => {
      setSeconds((s) => {
        if (s <= 1) {
          clearInterval(intervalRef.current);
          setRunning(false);
          playTimeUp();
          return 0;
        }
        // Tick on each of the last 10 seconds
        if (s <= 11) playTick();
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(intervalRef.current);
  }, [running]);

  const start = () => {
    playClick();
    if (seconds === 0) setSeconds(durationSeconds);
    setRunning(true);
  };
  const pause = () => {
    playClick();
    setRunning(false);
  };
  const reset = () => {
    playClick();
    setRunning(false);
    setSeconds(durationSeconds);
  };

  const warningThreshold = Math.min(30, Math.floor(durationSeconds / 4));
  const isWarning = seconds <= warningThreshold && seconds > 0;
  const isDone = seconds === 0;
  const pct = (seconds / durationSeconds) * 100;

  const bg = isDone ? "#FF2A54" : isWarning ? accentColor : "#111111";
  const textColor = isDone || isWarning ? "#111111" : accentColor;

  return (
    <div
      className="border-4 border-[#111111] shadow-brutal-md p-4"
      style={{ backgroundColor: bg }}
      data-testid={`${testIdPrefix}-card`}
    >
      <div
        className="flex items-center justify-between mb-1"
        style={{ color: textColor }}
      >
        <span className="font-mono-debate font-bold uppercase tracking-[0.2em] text-[10px] md:text-xs flex items-center gap-1">
          <Clock size={12} /> {label}
        </span>
        <span className="font-mono-debate font-bold uppercase tracking-[0.2em] text-[10px]">
          {isDone ? "TIME'S UP!" : running ? "LIVE" : "READY"}
        </span>
      </div>

      <motion.div
        animate={
          isDone
            ? { scale: [1, 1.06, 1] }
            : isWarning && running
              ? { scale: [1, 1.04, 1] }
              : { scale: 1 }
        }
        transition={{
          duration: isDone ? 0.6 : 1,
          repeat: isDone || (isWarning && running) ? Infinity : 0,
        }}
        className="font-anton text-5xl md:text-6xl text-center leading-none my-1"
        style={{
          color: textColor,
          textShadow: "3px 3px 0px rgba(0,0,0,0.25)",
        }}
        data-testid={`${testIdPrefix}-display`}
      >
        {formatTime(seconds)}
      </motion.div>

      {/* Progress bar */}
      <div className="h-2 bg-black/30 border-2 border-[#111111] mt-2 mb-3">
        <div
          className="h-full transition-all duration-300"
          style={{
            width: `${pct}%`,
            backgroundColor: isDone
              ? "#111111"
              : isWarning
                ? "#FF2A54"
                : accentColor,
          }}
        />
      </div>

      <div className="grid grid-cols-2 gap-2">
        {!running ? (
          <button
            onClick={start}
            data-testid={`${testIdPrefix}-start-btn`}
            className="btn-brutal-press bg-[#39FF14] text-[#111111] border-4 border-[#111111] shadow-brutal-sm font-anton uppercase text-sm md:text-base py-2 flex items-center justify-center gap-1"
          >
            <Play size={14} /> {seconds === durationSeconds ? "Start" : "Resume"}
          </button>
        ) : (
          <button
            onClick={pause}
            data-testid={`${testIdPrefix}-pause-btn`}
            className="btn-brutal-press bg-[#FFD700] text-[#111111] border-4 border-[#111111] shadow-brutal-sm font-anton uppercase text-sm md:text-base py-2 flex items-center justify-center gap-1"
          >
            <Pause size={14} /> Pause
          </button>
        )}
        <button
          onClick={reset}
          data-testid={`${testIdPrefix}-reset-btn`}
          className="btn-brutal-press bg-white text-[#111111] border-4 border-[#111111] shadow-brutal-sm font-anton uppercase text-sm md:text-base py-2 flex items-center justify-center gap-1"
        >
          <RotateCcw size={14} /> Reset
        </button>
      </div>

      {hint && (
        <p
          className="font-mono-debate text-[10px] uppercase tracking-widest text-center mt-3"
          style={{ color: textColor, opacity: 0.85 }}
        >
          {hint}
        </p>
      )}
    </div>
  );
};
